import { createMCPClient, type MCPClient } from '@ai-sdk/mcp';
import { type ToolSet } from 'ai';
import { createAuthorizationHeaderValue } from 'lib-shared-auth';

import { pickToolsByNamespace } from './toolSplit';

const DEFAULT_MAIN_PROMPT = 'chat-main-agent';
const DISCOVERY_API_PATH = 'v1/sidekick-config';
const MCP_PATH = 'v1/mcp';

/**
 * Called after a mutating tool succeeds, with the namespace the tool belongs
 * to. The consumer decides which cache tags that domain affects.
 */
export type OnMutation = (namespace: string | undefined) => void;

type DiscoveredSubagent = {
  name: string;
  prompt: string;
  namespaces: string[];
  stateCopy?: Record<string, string>;
};

type AgentDiscovery = {
  main: { prompt: string };
  subagents: DiscoveredSubagent[];
};

export type Subagent = {
  name: string;
  prompt: string;
  description: string;
  tools: ToolSet;
  stateCopy?: Record<string, string>;
};

export type SubagentMcpClient = {
  mainPrompt: string;
  subagents: Subagent[];
  hasError: boolean;
  dispose: () => Promise<void>;
};

const authorisedFetch: typeof fetch = async (input, init) => {
  const authorization = await createAuthorizationHeaderValue();
  const headers = new Headers(init?.headers);
  headers.set('Authorization', authorization);
  return fetch(input, { ...init, headers });
};

const fetchAgentDiscovery = async (
  restUrl: string,
): Promise<AgentDiscovery | null> => {
  try {
    const response = await authorisedFetch(`${restUrl}/${DISCOVERY_API_PATH}`);
    if (!response.ok) return null;
    return (await response.json()) as AgentDiscovery;
  } catch {
    return null;
  }
};

const connect = async (url: string): Promise<MCPClient | null> => {
  try {
    return await createMCPClient({
      transport: { type: 'http', url, fetch: authorisedFetch },
    });
  } catch {
    return null;
  }
};

type DiscoveredTools = {
  tools: ToolSet;
  namespaceByToolName: Map<string, string>;
};

const EMPTY_DISCOVERED_TOOLS: DiscoveredTools = {
  tools: {},
  namespaceByToolName: new Map(),
};

/** MCP checks for isError when checking the state of tool calls.
 * See https://modelcontextprotocol.io/specification/2025-11-25/schema#calltoolresult-iserror for more info.
 */
const hasFailed = (result: unknown): boolean =>
  typeof result === 'object' &&
  result !== null &&
  'isError' in result &&
  result.isError === true;

const withInvalidation = (
  capability: ToolSet[string],
  namespace: string | undefined,
  onMutation: OnMutation,
): ToolSet[string] => {
  const { execute } = capability;
  if (!execute) return capability;
  return {
    ...capability,
    execute: async (input, options) => {
      const result = await execute(input, options);
      const isSuccess = !hasFailed(result);
      if (isSuccess) onMutation(namespace);
      return result;
    },
  };
};

const discover = async (
  client: MCPClient,
  onMutation: OnMutation,
): Promise<DiscoveredTools> => {
  try {
    const definitions = await client.listTools();
    const namespaceByToolName = new Map<string, string>();
    const titleByToolName = new Map<string, string>();
    const mutationToolNames = new Set<string>();
    for (const definition of definitions.tools) {
      const namespace = definition._meta?.namespace;
      if (typeof namespace === 'string')
        namespaceByToolName.set(definition.name, namespace);
      if (typeof definition.title === 'string')
        titleByToolName.set(definition.name, definition.title);
      if (definition._meta?.isMutationTool)
        mutationToolNames.add(definition.name);
    }
    const discoveredTools = client.toolsFromDefinitions(definitions) as ToolSet;
    const tools = Object.fromEntries(
      Object.entries(discoveredTools).map(([name, capability]) => {
        const title = titleByToolName.get(name);
        const titled =
          title === undefined
            ? capability
            : { ...capability, metadata: { ...capability.metadata, title } };
        if (!mutationToolNames.has(name)) return [name, titled];
        const namespace = namespaceByToolName.get(name);
        return [name, withInvalidation(titled, namespace, onMutation)];
      }),
    ) as ToolSet;
    return { tools, namespaceByToolName };
  } catch {
    return EMPTY_DISCOVERED_TOOLS;
  }
};

/**
 * Transform 'input_streaming' to 'input-streaming'.
 */
const normalizeStateCopy = (
  stateCopy?: Record<string, string>,
): Record<string, string> | undefined => {
  if (!stateCopy) return undefined;
  return Object.fromEntries(
    Object.entries(stateCopy).map(([state, text]) => [
      state.replace(/_/g, '-'),
      text,
    ]),
  );
};

const subagentDescription = (tools: ToolSet): string => {
  return Object.entries(tools)
    .map(([name, capability]) => {
      const description =
        typeof capability.description === 'string'
          ? capability.description.trim()
          : undefined;
      return description ? `${name}: ${description}` : name;
    })
    .join('; ');
};
/**
 * Using specialized orchestration
 * https://ai-sdk.dev/docs/agents/subagents#specialized-orchestration, this
 * function calls both the discovery API and the MCP Server. The discovery API
 * returns a configuration informing us how to provision the subagents.
 */
export const createSubagentMcpClient = async (
  restUrl: string,
  onMutation: OnMutation,
): Promise<SubagentMcpClient> => {
  const [discovery, client] = await Promise.all([
    fetchAgentDiscovery(restUrl),
    connect(`${restUrl}/${MCP_PATH}`),
  ]);

  const { tools, namespaceByToolName } = client
    ? await discover(client, onMutation)
    : EMPTY_DISCOVERED_TOOLS;

  const hasError = !discovery || !client;

  const dispose = async (): Promise<void> => {
    if (client) await client.close();
  };

  if (!discovery) {
    return {
      mainPrompt: DEFAULT_MAIN_PROMPT,
      subagents: [],
      hasError,
      dispose,
    };
  }

  const subagents: Subagent[] = discovery.subagents.map((subagent) => {
    /**
     * By using the namespace from the discovery API and the available tools
     * from the MCP Server, we have enough info to provision the subagent with
     * it's assigned set of tools.
     */
    const subagentTools = pickToolsByNamespace(
      tools,
      namespaceByToolName,
      subagent.namespaces,
    );
    return {
      name: subagent.name,
      prompt: subagent.prompt,
      description: subagentDescription(subagentTools),
      tools: subagentTools,
      stateCopy: normalizeStateCopy(subagent.stateCopy),
    };
  });

  return { mainPrompt: discovery.main.prompt, subagents, hasError, dispose };
};
